import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import {
  AppId,
  AppSelectionStepProps,
} from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.types";
import { NotesApps } from "@/components/onboarding/components/WelcomeWizard/steps/import/ImportSteps.config";
import {
  FooterButton,
  FooterContainer,
  OtherInput,
  StepContent,
  OptionsSection,
  StepTitle,
  WizardContentWrapper,
  CheckableOption,
} from "@/components/onboarding/components/WelcomeWizard/WelcomeWizard.styles";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { logger } from "@/modules/logger";
import { MDSButtonVariant, MDSText, MDSTextVariant, MDSTextWeight } from "@/mds";

export const AppSelectionStep: React.FC<AppSelectionStepProps> = ({
  selectedAppId,
  customAppName,
  onAppSelect,
  onCustomAppNameChange,
  onNavToStep,
}) => {
  const otherInputRef = useRef<HTMLInputElement>(null);
  const isContinueButtonEnabled =
    selectedAppId === "other" ? customAppName.trim().length >= 1 : !!selectedAppId;

  const handleAppSelect = (appId: AppId) => {
    onAppSelect(appId);
  };

  const handleContinue = () => {
    if (!selectedAppId) {
      logger.error({ message: "handleContinue in AppSelectionStep called with no selectedAppId" });
      return;
    }

    trackEvent(TrackedEvent.ImportWizardAppSelect, {
      appId: selectedAppId,
      otherAppName:
        selectedAppId === "other"
          ? customAppName.toLowerCase().trim().replace(/\s+/g, " ") // lowercase and normalize whitespace
          : undefined,
    });

    switch (selectedAppId) {
      case "apple-notes":
        onNavToStep("apple-notes-1");
        break;
      case "obsidian":
        onNavToStep("obsidian-1");
        break;
      case "other":
        onNavToStep("other-apps-1");
        break;
      default:
        onNavToStep("skip-confirmation");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (isContinueButtonEnabled) {
        handleContinue();
      }
    }
  };

  // Auto-focus the input when "Other" is selected
  useEffect(() => {
    if (selectedAppId === "other" && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [selectedAppId]);

  return (
    <WizardContentWrapper onKeyDown={handleKeyDown}>
      <StepTitle>What notes app do you currently use?</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          Bring your existing notes into Mem so you can search through everything in one place and
          discover connections between your ideas. You can also choose to do this later.
        </MDSText>

        <OptionsSection>
          {NotesApps.map(app => (
            <CheckableOption
              key={app.id}
              isSelected={selectedAppId === app.id}
              onClick={() => handleAppSelect(app.id)}
              isOtherOption={app.id === "other"}
            >
              <AppOptionWrapper>
                {(app.iconImage || app.iconKind) && (
                  <AppIconWrapper>
                    {app.iconKind && <MdsIcon kind={app.iconKind} />}
                    {app.iconImage && <img src={app.iconImage} alt={app.title} />}
                  </AppIconWrapper>
                )}
                {app.id === "other" && selectedAppId === "other" ? (
                  <OtherInput
                    ref={otherInputRef}
                    placeholder="Enter your notes app name"
                    value={customAppName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      onCustomAppNameChange(e.target.value)
                    }
                    onClick={(e: React.MouseEvent) => e.stopPropagation()} // Prevent triggering the button click
                  />
                ) : (
                  <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                    {app.title}
                  </MDSText>
                )}
              </AppOptionWrapper>
              {selectedAppId === app.id && <MdsIcon kind={MdsIconKind.CheckCircleSolid} />}
            </CheckableOption>
          ))}
        </OptionsSection>
      </StepContent>

      <FooterContainer>
        <FooterButton
          onClick={handleContinue}
          variant={MDSButtonVariant.Contrast}
          isDisabled={!isContinueButtonEnabled}
        >
          Next
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
};

const AppIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  & img {
    width: 32px;
    height: 32px;
  }
`;

const AppOptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
`;
